/* eslint-disable no-console */
/* eslint-disable no-comments/disallowComments */
/**
 * This file contains authentication parameters. Contents of this file
 * is roughly the same across other MSAL.js libraries. These parameters
 * are used to initialize Angular and MSAL Angular configurations in
 * in app.module.ts file.
 */

import { InjectionToken } from '@angular/core';
import {
	LogLevel,
	Configuration,
	BrowserCacheLocation
} from '@azure/msal-browser';
import { environment } from '../../environments/environment';
import { B2CPolicies } from '../../environments/interfaces/environment.interface';

const isIE =
	window.navigator.userAgent.indexOf('MSIE ') > -1 ||
	window.navigator.userAgent.indexOf('Trident/') > -1;

const loggerLevel: number = environment.production ? LogLevel.Error : LogLevel.Info;
/**
 * Enter here the user flows and custom policies for your B2C application,
 * To learn more about user flows, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies: B2CPolicies = environment.b2cPolicies;

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
	auth: {
		clientId: environment.clientId, // This is the ONLY mandatory field that you need to supply.
		authority: b2cPolicies.authorities.signUpSignIn.authority, // Defaults to "https://login.microsoftonline.com/common"
		knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
		redirectUri: '/auth', // Points to window.location.origin. You must register this URI on Azure portal/App Registration.
		postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
		navigateToLoginRequestUrl: false // If "true", will navigate back to the original request location before processing the auth code response.
	},
	cache: {
		cacheLocation: BrowserCacheLocation.LocalStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
		storeAuthStateInCookie: isIE // Set this to "true" if you are having issues on IE11 or Edge
	},
	system: {
		loggerOptions: {
			loggerCallback() {},
			logLevel: loggerLevel,
			piiLoggingEnabled: false
		}
	}
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export interface ProtectedResources {
	endpoint: string;
	scopes: Array<string>;

}
export const protectedResources: ProtectedResources[] = [
	{
		endpoint: `${environment.baseUrl}v0/user/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/v1/user/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/role/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/equipment/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/myfleet/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/notification/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/auth`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/getEilTruck/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/getServiceHistory`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/getFinancials`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/aem/headers`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/aemfleetpage/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/aem-user-management/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/aem-user-management`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/service-history/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/order/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/dashboard/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/service-management/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/account/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/tips/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/v1/business-account/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/v1/aem-business-account`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/v1/equipment/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/v2/myquotes/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/v1/account-management/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/v1/aem-alert-banner`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/v1/aemquotes/quote-content-block`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/v1/aeminsights/insight-content-block`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/myinsights/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/subscription/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/domain/*`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/v1/aem-location-access-management`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/aem/get-service-history-count`,
		scopes: [environment.scope.read]
	},
	{
		endpoint: `${environment.baseUrl}v0/subscription/*`,
		scopes: [environment.scope.read]
	}
];

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export interface LoginRequest {
	scopes: Array<string>;
}
export const loginRequest: LoginRequest = {
	scopes: []
};

export interface AuthConfigRef {
	readonly b2cPolicies: B2CPolicies;
	readonly msalConfig: Configuration;
	readonly protectedResources: ProtectedResources[];
	readonly loginRequest: LoginRequest;
}
export class AuthConfig implements AuthConfigRef
{
	private pB2cPolicies: B2CPolicies;
	get b2cPolicies(): B2CPolicies
	{
		return this.pB2cPolicies;
	}
	private pMsalConfig: Configuration;
	get msalConfig(): Configuration
	{
		return this.pMsalConfig;
	}
	private pProtectedResources: ProtectedResources[];
	get protectedResources(): ProtectedResources[]
	{
		return this.pProtectedResources;
	}
	private pLoginRequest: LoginRequest;
	get loginRequest(): LoginRequest
	{
		return this.pLoginRequest;
	}

	constructor(configRef: AuthConfigRef)
	{
		this.pB2cPolicies = configRef.b2cPolicies;
		this.pMsalConfig = configRef.msalConfig;
		this.pProtectedResources = configRef.protectedResources;
		this.pLoginRequest = configRef.loginRequest;
	}
}

export const expAuthConfig = new InjectionToken<AuthConfig>('AuthConfigRef', {
	providedIn: 'root',
	factory: () =>
		new AuthConfig({
			b2cPolicies,
			msalConfig,
			protectedResources,
			loginRequest
		})
});
